import { Link, useLocation } from "react-router-dom";
import { Phone } from "lucide-react";

const SITES = [
    { to: "/", label: "Eclipse", key: "eclipse", short: "Corporativo" },
    { to: "/flexshade", label: "FlexShade", key: "flexshade", short: "Modular" },
    { to: "/velarias", label: "Skycover", key: "velarias", short: "Premium" },
];

// Resolves the "current site" from URL when no `active` prop is passed.
function resolveCurrent(pathname, active) {
    if (active) return active;
    if (pathname === "/") return "eclipse";
    if (pathname.startsWith("/flexshade")) return "flexshade";
    return "velarias";
}

// Inline switcher — minimal, sin iconos. Subraya el sitio activo con un color brand.
export default function CrossSiteNav({ active, className = "", accent = "#D60B07" }) {
    const location = useLocation();
    const current = resolveCurrent(location.pathname, active);
    return (
        <nav
            data-testid="cross-site-nav"
            aria-label="Ecosistema Eclipse Cubiertas"
            className={`inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] font-display font-medium ${className}`}
        >
            {SITES.map(({ to, label, key }, i) => {
                const isActive = current === key;
                return (
                    <span key={key} className="inline-flex items-center gap-3">
                        {i > 0 && <span aria-hidden className="text-white/20">·</span>}
                        <Link
                            to={to}
                            data-testid={`cross-site-${key}`}
                            aria-current={isActive ? "page" : undefined}
                            className={isActive ? "text-white border-b-2 pb-0.5" : "text-white/60 hover:text-white transition-colors"}
                            style={isActive ? { borderColor: accent } : undefined}
                        >
                            {label}
                        </Link>
                    </span>
                );
            })}
        </nav>
    );
}

// Utility bar fina sobre el navbar principal en /flexshade y /velarias.
// Solo desktop ≥ md. En mobile va dentro del Sheet via CrossSiteNavMobile.
export function SiteUtilityBar({ active, tel = "+52 81 8989 9822", telRaw = "528189899822", accent = "#D60B07", testId = "utility-bar" }) {
    return (
        <div
            data-testid={testId}
            className="hidden md:block bg-zinc-950/90 backdrop-blur-md border-b border-white/5"
        >
            <div className="container-eclipse h-9 flex items-center justify-between">
                <CrossSiteNav active={active} accent={accent} />
                <a
                    href={`tel:${telRaw}`}
                    data-testid={`${testId}-tel`}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-display font-medium text-white/70 hover:text-white transition-colors"
                >
                    <Phone className="w-3 h-3" strokeWidth={2.5} />
                    {tel}
                </a>
            </div>
        </div>
    );
}

// Variante mobile (dentro del Sheet). Stack simple, sin iconos.
// `accent` controla el background del item activo (default = rojo Eclipse).
export function CrossSiteNavMobile({ active, onNavigate, accent = "#D60B07" }) {
    const location = useLocation();
    const current = resolveCurrent(location.pathname, active);
    return (
        <div className="border-b border-neutral-200 pb-4 mb-2" data-testid="cross-site-nav-mobile">
            <p className="text-[10px] font-display font-bold tracking-[0.25em] text-neutral-500 mb-2 uppercase">Cambiar a otro sitio</p>
            <div className="space-y-1">
                {SITES.map(({ to, label, key, short }) => {
                    const isActive = current === key;
                    return (
                        <Link
                            key={key}
                            to={to}
                            onClick={onNavigate}
                            data-testid={`cross-site-mobile-${key}`}
                            style={isActive ? { backgroundColor: accent } : undefined}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                                isActive ? "text-white" : "text-neutral-700 hover:bg-neutral-100"
                            }`}
                        >
                            <div className="flex-1">
                                <div className="font-display font-bold text-sm leading-tight">{label}</div>
                                <div className={`text-[10px] uppercase tracking-wider ${isActive ? "text-white/80" : "text-neutral-500"}`}>{short}</div>
                            </div>
                            {isActive && <span className="text-[10px] font-bold uppercase tracking-wider">Aquí</span>}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
